import { Button } from '@appsumo/dorado-react';
import clsx from 'clsx';
import Link from 'next/link';
import { useMemo } from 'react';

import { DealImage } from '../../product';
import ProductDetailRating from './ProductDetailRating';

import { getPlanTypeText } from '~/lib/product/utils';
import { sentenceCase } from '~/lib/util';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

interface IPageQuery extends ParsedUrlQuery {
  slug: string;
  id?: string;
}

interface IPRoductDetails {
  deal: any;
  showDealRatings?: boolean;
}

function ProductDetails({ deal, showDealRatings = false }: IPRoductDetails) {
  const PDPUrl = `/products/${deal.slug}/`;
  const subHeader = deal.products[0]?.story?.subheader || '';
  const planTypeText = useMemo(
    () => sentenceCase(getPlanTypeText(deal?.unique_plan_types)),
    [deal],
  );

  const { asPath, query } = useRouter();

  const isAggregatePage =
    !(query as IPageQuery)?.id &&
    (asPath.startsWith(`${PDPUrl}reviews`) ||
      asPath.startsWith(`${PDPUrl}questions`));

  return (
    <div className="mt-4 md:mt-0">
      <div className="hidden md:block">
        <Link href={PDPUrl} className="max-sm:hidden">
          <DealImage
            deal={deal}
            priority={true}
            width={400}
            height={225}
            className="rounded"
          />
        </Link>
        <Link href={PDPUrl} className="max-sm:hidden">
          <h2 className="mt-4 font-header text-2xl font-bold leading-9">
            {deal.public_name}
          </h2>
        </Link>
        <p className="mb-2 font-header text-sm font-semibold text-forest">
          {planTypeText}
        </p>
        {deal.card_description && (
          <Link href={PDPUrl} className="max-sm:hidden">
            <p className="mb-4 line-clamp-2">{deal.card_description}</p>
          </Link>
        )}

        {subHeader && !deal.is_info && <p className="mb-4">{subHeader}</p>}

        {showDealRatings && (
          <ProductDetailRating
            dealReview={deal.deal_review}
            reviewUrl={`${PDPUrl}reviews`}
            PDPUrl={PDPUrl}
          />
        )}

        {!isAggregatePage && (
          <Button
            href={PDPUrl}
            className={clsx('inline-block', {
              'mb-4': !deal.top_5_questions?.length,
            })}
          >
            Product details
          </Button>
        )}

        {/* Not sure why React doesn't load the aggregate page when using back button of the browser
            applying a normal HTML link as a workaround for now. 
        */}
        {isAggregatePage && (
          <a
            className="inline-block rounded-full border-2 border-transparent bg-dorado px-6 py-2 font-header font-bold text-midnight hover:border-lemon hover:bg-lemon focus-visible:border-black-pearl focus-visible:bg-lemon focus-visible:outline-none active:border-sunset active:bg-sunset"
            href={PDPUrl}
            rel="noreferrer noopener"
          >
            Product details
          </a>
        )}
      </div>
    </div>
  );
}

export default ProductDetails;
